import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnChanges, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { TimerService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { take } from 'rxjs';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { isBetBuilderPick } from '../../../../core/utils';
import {
    IPlaceButtonStateService,
    PLACE_BUTTON_STATE_SERVICE,
} from '../../../betplacement/services/place-button/viewmodel/interfaces/place-button-interfaces';
import DigitalSettingsActions from '../../../settings/digital-actions';
import { EntainUiBetBuilderPickRemovalTracking } from '../../../tracking/tracking.models';
import BetslipPickActions from '../../actions';
import { betslipPicksListSelector } from '../../selectors';
import BetslipPickUtilsService from '../../services/betslip-pick-utils.service';

@Component({
    selector: 'bs-digital-picks-toolbar',
    templateUrl: './picks-toolbar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./picks-toolbar.component.scss'],
    standalone: false,
})
export default class PicksToolbarComponent implements OnChanges, OnDestroy {
    private static defaultConfirmationStateTimeout = 4000;
    private readonly requestRemovePicksTimeout: number;
    private removeTimoutHandler: number;
    readonly messages: {
        selectAllTooltip: string;
        bettingOptionsTooltip: string;
        removeAllMessage: string;
        deleteAllTooltip: string;
    };

    @Input() picks: Record<string, { isSelected: boolean }>;
    @Input() isTeaserBet = false;
    @HostBinding('class') className = 'betslip-picks-toolbar';
    allPicksSelected = true;
    confirmRemoveAll = false;
    module = EpcotModule.Betslip;
    hideToolbarActions = false;
    hasSgpBet = false;

    private removing = false;

    constructor(
        private store: Store,
        private timerService: TimerService,
        private changeDetector: ChangeDetectorRef,
        private trackingService: TrackingService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        betslipConfig: BetslipConfig,
        @Inject(PLACE_BUTTON_STATE_SERVICE) private placeButtonService: IPlaceButtonStateService,
        private betslipPickUtilsService: BetslipPickUtilsService,
    ) {
        this.requestRemovePicksTimeout = betslipConfig.removeAllPicksConfirmationTimeout || PicksToolbarComponent.defaultConfirmationStateTimeout;
        const commonMessages = messageProvider.getMessages().common;
        this.messages = {
            selectAllTooltip: commonMessages.SelectAll,
            bettingOptionsTooltip: commonMessages.BettingOptions,
            removeAllMessage: commonMessages.DeleteAllConfirmation,
            deleteAllTooltip: commonMessages.DeleteAll,
        };
        this.placeButtonService.state$.pipe(takeUntilDestroyed()).subscribe((state) => {
            this.hideToolbarActions = state.isProcessing;
            changeDetector.markForCheck();
        });
    }

    ngOnChanges(): void {
        this.allPicksSelected = Object.values(this.picks).every((p) => p.isSelected);
    }

    ngOnDestroy(): void {
        clearTimeout(this.removeTimoutHandler);
    }

    togglePicksSelection(isChecked: boolean): void {
        if (isChecked !== this.allPicksSelected) {
            this.store.dispatch(BetslipPickActions.toggleAllPicks({ select: !this.allPicksSelected }));

            if (!this.allPicksSelected) {
                this.trackActions('Select all', 'Select-all click');
            }
        }
    }

    requestRemoveAllPicks(): void {
        this.checkAndEnableEntainUiBetBuilderAllPickRemovedTracking();
        if (this.confirmRemoveAll && !this.removing) {
            this.removing = true;
            this.confirmRemoveAll = false;
            clearTimeout(this.removeTimoutHandler); // It is not 100% needed because when remove all picks, a destroy handler will be called.
            // we are scheduling the remove action to have a good INP value on the click event
            this.timerService.setTimeout(() => {
                this.store.dispatch(BetslipPickActions.requestRemoveAllPicks());
                this.removing = false;
            });
            // furthermore we are separating the state change from the tracking actions
            this.timerService.scheduleIdleCallback(() => {
                if (this.hasSgpBet) {
                    this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingDeleteConfirmation);
                } else {
                    this.trackActions(EntainUiBetBuilderPickRemovalTracking.TrackingDeleteConfirmation, NOT_APPLICABLE);
                }
            });
        } else if (!this.confirmRemoveAll) {
            this.confirmRemoveAll = true;
            this.removeTimoutHandler = setTimeout(() => {
                this.confirmRemoveAll = false;
                this.changeDetector.markForCheck();
            }, this.requestRemovePicksTimeout) as unknown as number; // ts gets nodejs setTimeout typings
            this.timerService.scheduleIdleCallback(() => {
                if (this.hasSgpBet) {
                    this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(
                        EntainUiBetBuilderPickRemovalTracking.TrackingAllPickRemovalBetslip,
                    );
                } else {
                    this.trackActions(EntainUiBetBuilderPickRemovalTracking.TrackingAllPickRemovalBetslip, NOT_APPLICABLE);
                }
            });
        }
    }

    openSettingsDialog(): void {
        this.trackActions('Settings - icon', 'icon - click');
        this.store.dispatch(DigitalSettingsActions.openSettings());
    }

    private trackActions(labelEvent: string, eventDetails: string) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betslip',
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    private checkAndEnableEntainUiBetBuilderAllPickRemovedTracking = () => {
        return this.store
            .select(betslipPicksListSelector)
            .pipe(take(1))
            .subscribe((picks) => (this.hasSgpBet = Boolean(picks && picks.length && picks.some((pick) => isBetBuilderPick(pick)))));
    };
}
